import { useState, useEffect, useRef } from 'react';
import {
    defaultConsultConfig,
    consultWithCustomQuoteConfig,
    consultWithAskQuestionSeeAvailableTimes,
} from '../components/forms/consult/consultFormConfigs';

// Extend Window interface to allow Optimizely to set a synchronous override flag
// before React mounts, so the hook can pick it up on initialization.
declare global {
    interface Window {
        __formConfigOverride?: string;
    }
}

type ConsultFormConfig =
    | typeof defaultConsultConfig
    | typeof consultWithCustomQuoteConfig
    | typeof consultWithAskQuestionSeeAvailableTimes;

const CONFIG_MAP: Record<string, ConsultFormConfig> = {
    defaultConsultConfig,
    consultWithCustomQuoteConfig,
    consultWithAskQuestionSeeAvailableTimes,
};

const EVENT_NAME = 'form_config_override';

/**
 * Allows Optimizely (or any other caller) to swap the ConsultationForm config at runtime
 * by dispatching a CustomEvent or setting window.__formConfigOverride before React mounts.
 *
 * Example usage from Optimizely campaign JS:
 *   window.__formConfigOverride = 'consultWithAskQuestionSeeAvailableTimes';
 *   window.dispatchEvent(new CustomEvent('form_config_override', { detail: 'consultWithAskQuestionSeeAvailableTimes' }));
 */
export function useConsultFormConfig(defaultConfig: ConsultFormConfig): ConsultFormConfig {
    const defaultConfigRef = useRef(defaultConfig);
    defaultConfigRef.current = defaultConfig;

    const resolveConfig = (name: string | undefined): ConsultFormConfig =>
        (name && CONFIG_MAP[name]) || defaultConfigRef.current;

    const [config, setConfig] = useState<ConsultFormConfig>(() => {
        // Read synchronous override set by Optimizely before React mounted
        if (typeof window !== 'undefined') {
            return resolveConfig(window.__formConfigOverride);
        }
        return defaultConfig;
    });

    useEffect(() => {
        if (typeof window === 'undefined') return;

        const handler = (event: Event) => {
            const name = (event as CustomEvent<string>).detail;
            setConfig(resolveConfig(name));
        };

        window.addEventListener(EVENT_NAME, handler);
        return () => window.removeEventListener(EVENT_NAME, handler);
    }, []);

    return config;
}
